// src/pages/PrivacyPolicy.jsx
import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const PrivacyContainer = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.background.primary};
  padding: 8rem 0 4rem;
`;

const ContentWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const PageHeader = styled(motion.div)`
  text-align: center;
  margin-bottom: 4rem;
`;

const PageTitle = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  background: ${({ theme }) => theme.gradients.primary};
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 1rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 2.5rem;
  }
`;

const LastUpdated = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 1rem;
`;

const Content = styled(motion.div)`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 20px;
  padding: 3rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  backdrop-filter: blur(10px);
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 2rem;
  }
`;

const Section = styled.section`
  margin-bottom: 2.5rem;
  
  h2 {
    color: ${({ theme }) => theme.colors.primary.yellow};
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 1rem;
    border-bottom: 2px solid ${({ theme }) => theme.colors.primary.yellow};
    padding-bottom: 0.5rem;
  }
  
  h3 {
    color: ${({ theme }) => theme.colors.text.primary};
    font-size: 1.2rem;
    font-weight: 600;
    margin: 1.5rem 0 0.75rem;
  }
  
  p {
    color: ${({ theme }) => theme.colors.text.secondary};
    line-height: 1.7;
    margin-bottom: 1rem;
  }
  
  ul, ol {
    color: ${({ theme }) => theme.colors.text.secondary};
    margin: 1rem 0;
    padding-left: 2rem;
    
    li {
      margin-bottom: 0.5rem;
      line-height: 1.6;
    }
  }
  
  strong {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const ContactBox = styled.div`
  background: ${({ theme }) => theme.gradients.cyber};
  border-radius: 15px;
  padding: 2rem;
  margin-top: 2rem;
  border: 1px solid ${({ theme }) => theme.colors.primary.purple};
  
  h3 {
    color: ${({ theme }) => theme.colors.primary.yellow};
    margin-bottom: 1rem;
  }
  
  p {
    color: ${({ theme }) => theme.colors.text.primary};
    margin-bottom: 0.5rem;
  }
  
  a {
    color: ${({ theme }) => theme.colors.primary.yellow};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const PrivacyPolicy = () => {
  return (
    <PrivacyContainer>
      <ContentWrapper>
        <PageHeader
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <PageTitle>Privacy Policy</PageTitle>
          <LastUpdated>Last Updated: January 2025</LastUpdated>
        </PageHeader>

        <Content
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <Section>
            <h2>1. Introduction</h2>
            <p>
              Security BSides Albuquerque ("we," "our," or "us") is committed to protecting your privacy. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your 
              information when you visit our website, register for our conference, or interact 
              with our services related to the Security BSides Albuquerque cybersecurity conference.
            </p>
            <p>
              Security BSides Albuquerque is a community-driven, non-profit cybersecurity conference 
              that brings together security professionals, researchers, and enthusiasts in 
              New Mexico and beyond.
            </p>
          </Section>

          <Section>
            <h2>2. Information We Collect</h2>
            
            <h3>Personal Information</h3>
            <p>We may collect personal information that you voluntarily provide to us when you:</p>
            <ul>
              <li>Register for the Security BSides Albuquerque conference</li>
              <li>Subscribe to our newsletter or mailing list</li>
              <li>Submit a speaking proposal or workshop application</li>
              <li>Contact us through our website or email</li>
              <li>Participate in conference surveys or feedback forms</li>
              <li>Apply for sponsorship opportunities</li>
            </ul>
            
            <p>This information may include:</p>
            <ul>
              <li>Name and contact information (email, phone number)</li>
              <li>Professional information (job title, company, industry)</li>
              <li>Conference preferences and interests</li>
              <li>Dietary restrictions or accessibility needs</li>
              <li>Payment information (processed securely through third-party providers)</li>
            </ul>

            <h3>Automatically Collected Information</h3>
            <p>We may automatically collect certain information when you visit our website:</p>
            <ul>
              <li>IP address and browser information</li>
              <li>Device information and operating system</li>
              <li>Website usage patterns and analytics</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>
          </Section>

          <Section>
            <h2>3. How We Use Your Information</h2>
            <p>We use the information we collect for the following purposes:</p>
            <ul>
              <li><strong>Conference Management:</strong> Registration, check-in, and event logistics</li>
              <li><strong>Communication:</strong> Sending conference updates, schedules, and important announcements</li>
              <li><strong>Content Delivery:</strong> Providing access to talks, workshops, and conference materials</li>
              <li><strong>Safety & Security:</strong> Ensuring a safe and secure conference environment</li>
              <li><strong>Networking:</strong> Facilitating connections between attendees (with consent)</li>
              <li><strong>Improvement:</strong> Analyzing feedback to improve future conferences</li>
              <li><strong>Marketing:</strong> Promoting future Security BSides Albuquerque events (opt-in basis)</li>
            </ul>
          </Section>

          <Section>
            <h2>4. Information Sharing and Disclosure</h2>
            <p>We do not sell, trade, or rent your personal information to third parties. We may share your information in the following circumstances:</p>
            
            <h3>Conference Partners and Sponsors</h3>
            <ul>
              <li>Sponsor contact (only with explicit opt-in consent)</li>
              <li>Venue requirements for safety and security</li>
              <li>Catering services for dietary accommodations</li>
            </ul>

            <h3>Service Providers</h3>
            <ul>
              <li>Registration platforms (Eventbrite, etc.)</li>
              <li>Email marketing services (with your consent)</li>
              <li>Payment processors for secure transactions</li>
              <li>Website hosting and analytics providers</li>
            </ul>

            <h3>Legal Requirements</h3>
            <p>We may disclose your information if required by law or in good faith belief that such action is necessary to comply with legal obligations.</p>
          </Section>

          <Section>
            <h2>5. Photography and Recording</h2>
            <p>
              Security BSides Albuquerque may photograph, video record, or audio record conference activities 
              for promotional, educational, and archival purposes. By attending the conference, 
              you consent to such recording and the use of your likeness in conference materials.
            </p>
            <p>
              If you do not wish to be photographed or recorded, please contact us at 
              <a href="mailto:privacy@bsidesabq.org">privacy@bsidesabq.org</a> and we will 
              make reasonable efforts to accommodate your request.
            </p>
          </Section>

          <Section>
            <h2>6. Data Security</h2>
            <p>
              We implement appropriate technical and organizational security measures to protect 
              your personal information against unauthorized access, alteration, disclosure, or 
              destruction. However, no internet transmission is 100% secure, and we cannot 
              guarantee absolute security.
            </p>
            <ul>
              <li>Secure HTTPS encryption for our website</li>
              <li>Limited access to personal information on a need-to-know basis</li>
              <li>Regular security reviews and updates</li>
              <li>Secure disposal of personal information when no longer needed</li>
            </ul>
          </Section>

          <Section>
            <h2>7. Your Rights and Choices</h2>
            <p>You have the following rights regarding your personal information:</p>
            <ul>
              <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
              <li><strong>Correction:</strong> Request correction of inaccurate personal information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information</li>
              <li><strong>Opt-out:</strong> Unsubscribe from marketing communications at any time</li>
              <li><strong>Portability:</strong> Request a copy of your data in a portable format</li>
            </ul>
            <p>
              To exercise these rights, please contact us at 
              <a href="mailto:privacy@bsidesabq.org">privacy@bsidesabq.org</a>.
            </p>
          </Section>

          <Section>
            <h2>8. Cookies and Tracking</h2>
            <p>
              Our website uses cookies and similar technologies to enhance your browsing experience, 
              analyze website traffic, and understand user preferences. You can control cookie 
              settings through your browser preferences.
            </p>
            <p>We use cookies for:</p>
            <ul>
              <li>Essential website functionality</li>
              <li>Analytics and performance monitoring</li>
              <li>User preferences and settings</li>
              <li>Security and fraud prevention</li>
            </ul>
          </Section>

          <Section>
            <h2>9. Children's Privacy</h2>
            <p>
              Security BSides Albuquerque is not intended for children under 13 years of age. We do not 
              knowingly collect personal information from children under 13. If you are a parent 
              or guardian and believe your child has provided us with personal information, 
              please contact us immediately.
            </p>
          </Section>

          <Section>
            <h2>10. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our 
              practices or for other operational, legal, or regulatory reasons. We will notify 
              you of any material changes by posting the updated policy on our website and 
              updating the "Last Updated" date.
            </p>
          </Section>

          <ContactBox>
            <h3>Contact Us</h3>
            <p>
              If you have any questions about this Privacy Policy or our data practices, 
              please contact us at:
            </p>
            <p>
              <strong>Email:</strong> <a href="mailto:privacy@bsidesabq.org">privacy@bsidesabq.org</a><br />
              <strong>General Contact:</strong> <a href="mailto:info@bsidesabq.org">info@bsidesabq.org</a><br />
              <strong>Website:</strong> <a href="https://bsidesabq.org">https://bsidesabq.org</a>
            </p>
            <p>
              <strong>Security BSides Albuquerque</strong><br />
              Albuquerque, New Mexico, USA
            </p>
          </ContactBox>
        </Content>
      </ContentWrapper>
    </PrivacyContainer>
  );
};

export default PrivacyPolicy;